import type { BiomeProfile } from './BiomeProfile';

/**
 * Per-world biome data. Every species, every flora archetype and every
 * weather signature is hand-picked from the user's reimaginação:
 *
 *   1. Caelis (rose)        — pink salt desert, delicate and hostile
 *   2. Verdia (mint)        — biological forest, pulsing crust
 *   3. Ambre (peach)        — iron-rich desert under amber rings
 *   4. Auron (butter)       — vitrified gold plains, glowing core
 *   5. Solenne (lavender)   — frozen sea, crystal storms
 *   6. Nyara (mint)         — cold tempest highlands
 *   7. Verdia Minor (butter)— stripped mineral moon
 *   8. Solenne Echo (rose)  — luminous photophore moon
 *   9. Nyara Whisper (peach)— radioactive shadow moon
 *
 * Counts are tuned for a 2400u disc with InstancedMesh rendering — densities
 * are set so every walking radius reads as a real ecosystem rather than an
 * empty plain. Distribution is uniform-area; no inner-ring concentration.
 */

const PROFILES: Record<string, BiomeProfile> = {
  // ────────────── PLANETAS ──────────────

  Caelis: {
    name: 'Caelis',
    fogColor: 0xf6cfd9,
    ambientWarm: 0xffd9e2,
    ambientCool: 0xc995a8,
    flora: [
      // arbustos de sal cristalizado com formas suaves
      {
        archetype: 'rust-shrub',
        count: 2200,
        minScale: 0.7,
        maxScale: 1.4,
        tint: 0xe7a4b3,
        accent: 0xfff0f4,
        emissive: 0xffcfdc,
        emissiveStrength: 0.12,
        clusterChance: 0.35
      },
      // flores-cálice rosadas
      {
        archetype: 'salt-bloom',
        count: 3000,
        minScale: 0.55,
        maxScale: 1.2,
        tint: 0xf6c5cf,
        accent: 0xffe6ef,
        emissive: 0xffd6df,
        emissiveStrength: 0.1,
        clusterChance: 0.45
      },
      // cactos translúcidos bulbosos
      {
        archetype: 'glass-cactus',
        count: 1600,
        minScale: 0.85,
        maxScale: 1.7,
        tint: 0xf6dadf,
        accent: 0xc16d8a,
        clusterChance: 0.3
      },
      // tapetes minerais claros
      {
        archetype: 'mineral-mat',
        count: 2600,
        minScale: 0.9,
        maxScale: 1.9,
        tint: 0xf2bdc9,
        accent: 0xffe5ec,
        altitudeBias: -1
      }
    ],
    fauna: [
      {
        archetype: 'crawler',
        temperament: 'passive',
        count: 120,
        minScale: 0.45,
        maxScale: 0.7,
        bodyTint: 0xc77b8c,
        accent: 0xffd6df,
        displayName: 'Corredor das Salinas'
      },
      {
        archetype: 'walker',
        temperament: 'skittish',
        count: 88,
        minScale: 0.7,
        maxScale: 1.05,
        bodyTint: 0xe79bb1,
        accent: 0xffe2ea,
        displayName: 'Rastejante de Cristal'
      },
      {
        archetype: 'glider',
        temperament: 'passive',
        count: 72,
        minScale: 0.75,
        maxScale: 1.0,
        bodyTint: 0xd47995,
        accent: 0xffaecf,
        emissive: 0xff9bb6,
        emissiveStrength: 0.45,
        displayName: 'Planador Magenta',
        hoverHeight: 12
      }
    ],
    water: {
      level: -10.0,
      surfaceTint: 0xf6c8d3,
      depthTint: 0x9c4f68,
      foamTint: 0xffe2ea,
      emissive: 0xffb6cb,
      emissiveStrength: 0.18,
      waveAmplitude: 0.18,
      waveSpeed: 0.55,
      poolRadius: 320
    },
    weather: {
      kind: 'salt-gust',
      density: 540,
      tint: 0xfde3ec,
      accent: 0xf2b3c5,
      drift: 6.5,
      fall: 0.4,
      size: 0.55
    }
  },

  Verdia: {
    name: 'Verdia',
    fogColor: 0xc8ecd2,
    ambientWarm: 0xd8f3df,
    ambientCool: 0x6fa285,
    flora: [
      // árvores-vesícula
      {
        archetype: 'vesicle-tree',
        count: 1400,
        minScale: 1.4,
        maxScale: 2.8,
        tint: 0x7dc996,
        accent: 0xc4f0d3,
        emissive: 0x90e8b1,
        emissiveStrength: 0.18,
        clusterChance: 0.32,
        minDistanceFromCenter: 24
      },
      // frondes carnudas
      {
        archetype: 'fleshy-frond',
        count: 3000,
        minScale: 0.7,
        maxScale: 1.6,
        tint: 0x8ed7a8,
        accent: 0xb5edc5
      },
      // raízes pulsantes expostas
      {
        archetype: 'pulsing-root',
        count: 1600,
        minScale: 0.9,
        maxScale: 1.7,
        tint: 0x4f8a6a,
        accent: 0x8ed7a8,
        emissive: 0x90f0b1,
        emissiveStrength: 0.55,
        clusterChance: 0.4
      },
      // flores de néctar mineral
      {
        archetype: 'nectar-bell',
        count: 2000,
        minScale: 0.55,
        maxScale: 1.05,
        tint: 0xc4f0d3,
        accent: 0xfcf6cb,
        emissive: 0xfffaa0,
        emissiveStrength: 0.5,
        clusterChance: 0.45
      }
    ],
    fauna: [
      {
        archetype: 'walker',
        temperament: 'passive',
        count: 140,
        minScale: 0.85,
        maxScale: 1.3,
        bodyTint: 0x7fbf95,
        accent: 0xc4f0d3,
        displayName: 'Pastador Menta'
      },
      {
        archetype: 'glider',
        temperament: 'passive',
        count: 108,
        minScale: 0.6,
        maxScale: 0.9,
        bodyTint: 0xb6e6c8,
        accent: 0xfcf3a8,
        emissive: 0xfff3a0,
        emissiveStrength: 0.55,
        displayName: 'Polinizador Flutuante',
        hoverHeight: 8
      },
      {
        archetype: 'predator',
        temperament: 'hostile',
        count: 24,
        minScale: 1.1,
        maxScale: 1.45,
        bodyTint: 0x4e8a6a,
        accent: 0xa5e6bd,
        emissive: 0x7af3a8,
        emissiveStrength: 0.4,
        displayName: 'Guardião da Crosta'
      }
    ],
    water: {
      level: -8.5,
      surfaceTint: 0x6fc4b0,
      depthTint: 0x18584f,
      foamTint: 0xd1f3df,
      waveAmplitude: 0.34,
      waveSpeed: 0.85,
      poolRadius: 600
    },
    weather: {
      kind: 'spore-haze',
      density: 700,
      tint: 0xc4f0d3,
      accent: 0xfff3a0,
      drift: 1.4,
      fall: -0.4,
      size: 0.45,
      pulse: 0.3
    }
  },

  Ambre: {
    name: 'Ambre',
    fogColor: 0xf5c7a4,
    ambientWarm: 0xffd5b3,
    ambientCool: 0xa05f3a,
    flora: [
      // torres minerais ocres com aparência orgânica
      {
        archetype: 'ferrous-spire',
        count: 1400,
        minScale: 1.5,
        maxScale: 3.6,
        tint: 0xc06b3e,
        accent: 0xf79e78,
        clusterChance: 0.25
      },
      // arbustos ferruginosos curvos
      {
        archetype: 'rust-shrub',
        count: 3000,
        minScale: 0.7,
        maxScale: 1.5,
        tint: 0xd47a4c,
        accent: 0xffd1b8
      },
      // flores âmbar fossilizadas
      {
        archetype: 'amber-blossom',
        count: 2000,
        minScale: 0.55,
        maxScale: 1.0,
        tint: 0xffb27a,
        accent: 0xffe2c4,
        emissive: 0xff9b58,
        emissiveStrength: 0.35,
        clusterChance: 0.45
      },
      // vegetação de pedra suave (mat com pétalas)
      {
        archetype: 'mineral-mat',
        count: 1500,
        minScale: 1.0,
        maxScale: 2.0,
        tint: 0xb55a31,
        accent: 0xffc59c,
        altitudeBias: -1
      }
    ],
    fauna: [
      {
        archetype: 'crawler',
        temperament: 'passive',
        count: 140,
        minScale: 0.5,
        maxScale: 0.85,
        bodyTint: 0xc1623a,
        accent: 0xffd1b8,
        displayName: 'Lagarto Ferroso'
      },
      {
        archetype: 'walker',
        temperament: 'skittish',
        count: 88,
        minScale: 0.8,
        maxScale: 1.15,
        bodyTint: 0xe48a5e,
        accent: 0xffd5b3,
        displayName: 'Escavador Âmbar'
      },
      {
        archetype: 'predator',
        temperament: 'hostile',
        count: 24,
        minScale: 1.1,
        maxScale: 1.4,
        bodyTint: 0x8a3318,
        accent: 0xff7a3c,
        emissive: 0xff7a3c,
        emissiveStrength: 0.45,
        displayName: 'Predador de Anel'
      }
    ],
    water: {
      level: -9.0,
      surfaceTint: 0xe88a4c,
      depthTint: 0x5a2110,
      foamTint: 0xffd1a4,
      emissive: 0xff8b3c,
      emissiveStrength: 0.25,
      waveAmplitude: 0.16,
      waveSpeed: 0.45,
      poolRadius: 240
    },
    weather: {
      kind: 'rust-dust',
      density: 760,
      tint: 0xd17a4a,
      accent: 0xffd1b8,
      drift: 8.0,
      fall: 0.8,
      size: 0.6
    }
  },

  Auron: {
    name: 'Auron',
    fogColor: 0xfff2c2,
    ambientWarm: 0xfff5c1,
    ambientCool: 0xb88d2a,
    flora: [
      // lâminas de vidro orgânico curvadas
      {
        archetype: 'glass-blade-fan',
        count: 2000,
        minScale: 1.2,
        maxScale: 2.6,
        tint: 0xfdd96d,
        accent: 0xfffbe6,
        emissive: 0xfff0a0,
        emissiveStrength: 0.18,
        clusterChance: 0.45
      },
      // flores metálicas douradas
      {
        archetype: 'metal-flower',
        count: 2400,
        minScale: 0.55,
        maxScale: 1.05,
        tint: 0xffe089,
        accent: 0xfffbe6,
        emissive: 0xfff8c1,
        emissiveStrength: 0.32
      },
      // arbustos prateados baixos
      {
        archetype: 'rust-shrub',
        count: 1600,
        minScale: 0.55,
        maxScale: 1.0,
        tint: 0xd6cfb0,
        accent: 0xfffbe6
      },
      // bulbos de calor
      {
        archetype: 'heat-bulb',
        count: 1700,
        minScale: 0.7,
        maxScale: 1.3,
        tint: 0xfff2a0,
        accent: 0xff9b58,
        emissive: 0xffb55a,
        emissiveStrength: 0.6,
        clusterChance: 0.5
      }
    ],
    fauna: [
      {
        archetype: 'walker',
        temperament: 'passive',
        count: 120,
        minScale: 0.85,
        maxScale: 1.3,
        bodyTint: 0xd9b25a,
        accent: 0xfff5c1,
        emissive: 0xffe089,
        emissiveStrength: 0.18,
        displayName: 'Ungulado de Vidro'
      },
      {
        archetype: 'glider',
        temperament: 'passive',
        count: 88,
        minScale: 0.65,
        maxScale: 0.95,
        bodyTint: 0xf2cb6c,
        accent: 0xfff5c1,
        emissive: 0xffe089,
        emissiveStrength: 0.55,
        displayName: 'Coletor de Brilho',
        hoverHeight: 10
      },
      {
        archetype: 'predator',
        temperament: 'hostile',
        count: 24,
        minScale: 1.15,
        maxScale: 1.5,
        bodyTint: 0xa07020,
        accent: 0xffd068,
        emissive: 0xff8b18,
        emissiveStrength: 0.55,
        displayName: 'Guardião Térmico'
      }
    ],
    water: {
      level: -9.0,
      surfaceTint: 0xfdd96d,
      depthTint: 0x5d4011,
      foamTint: 0xfffbe6,
      emissive: 0xfff0a0,
      emissiveStrength: 0.4,
      waveAmplitude: 0.14,
      waveSpeed: 0.4,
      poolRadius: 380
    },
    weather: {
      kind: 'heat-shimmer',
      density: 480,
      tint: 0xfff5c1,
      accent: 0xffd86b,
      drift: 1.0,
      fall: -0.2,
      size: 0.45,
      pulse: 0.2
    }
  },

  Solenne: {
    name: 'Solenne',
    fogColor: 0xd6c5f0,
    ambientWarm: 0xe1d1f7,
    ambientCool: 0x4d3e80,
    flora: [
      // espinhos de gelo floral curvos
      {
        archetype: 'ice-spire',
        count: 2000,
        minScale: 1.3,
        maxScale: 3.0,
        tint: 0xcfbbf0,
        accent: 0xf4ecff,
        emissive: 0xb5a2f0,
        emissiveStrength: 0.3,
        clusterChance: 0.45
      },
      // cristais-pétala
      {
        archetype: 'crystal-petal',
        count: 2800,
        minScale: 0.6,
        maxScale: 1.2,
        tint: 0xb4d0ff,
        accent: 0xf4ecff,
        emissive: 0xc9b8ff,
        emissiveStrength: 0.55
      },
      // líquens lilases translúcidos
      {
        archetype: 'frost-moss',
        count: 2000,
        minScale: 1.0,
        maxScale: 2.0,
        tint: 0x9a86d4,
        accent: 0xe1d1f7,
        emissive: 0xb5a2f0,
        emissiveStrength: 0.35,
        altitudeBias: -1
      }
    ],
    fauna: [
      {
        archetype: 'walker',
        temperament: 'passive',
        count: 100,
        minScale: 0.85,
        maxScale: 1.2,
        bodyTint: 0x8a6dc4,
        accent: 0xcfbbf0,
        emissive: 0xb5a2f0,
        emissiveStrength: 0.25,
        displayName: 'Andarilho do Gelo'
      },
      {
        archetype: 'glider',
        temperament: 'skittish',
        count: 100,
        minScale: 0.7,
        maxScale: 1.0,
        bodyTint: 0xb4d0ff,
        accent: 0xf4ecff,
        emissive: 0xd1c1ff,
        emissiveStrength: 0.65,
        displayName: 'Voador de Cristal',
        hoverHeight: 14
      },
      {
        archetype: 'predator',
        temperament: 'hostile',
        count: 24,
        minScale: 1.15,
        maxScale: 1.5,
        bodyTint: 0x4a3a82,
        accent: 0xb4d0ff,
        emissive: 0xff66c8,
        emissiveStrength: 0.55,
        displayName: 'Caçador de Plasma'
      }
    ],
    water: {
      level: -8.0,
      surfaceTint: 0xb4d0ff,
      depthTint: 0x2a2058,
      foamTint: 0xf4ecff,
      emissive: 0xc9b8ff,
      emissiveStrength: 0.25,
      waveAmplitude: 0.22,
      waveSpeed: 0.55,
      ice: true,
      poolRadius: 800
    },
    weather: {
      kind: 'crystal-storm',
      density: 760,
      tint: 0xcfbbf0,
      accent: 0xf4ecff,
      drift: 4.0,
      fall: 1.4,
      size: 0.55,
      pulse: 0.25
    }
  },

  Nyara: {
    name: 'Nyara',
    fogColor: 0xc8e2d4,
    ambientWarm: 0xd6f0e0,
    ambientCool: 0x3a6b58,
    flora: [
      // musgos gelados luminescentes
      {
        archetype: 'frost-moss',
        count: 2200,
        minScale: 1.1,
        maxScale: 2.2,
        tint: 0x7faf95,
        accent: 0xc4f0d3,
        emissive: 0x95f0bf,
        emissiveStrength: 0.4,
        altitudeBias: -1
      },
      // arbustos de gelo fibroso
      {
        archetype: 'fibrous-ice-shrub',
        count: 1900,
        minScale: 0.85,
        maxScale: 1.6,
        tint: 0x9bc7ad,
        accent: 0xeffaf2,
        emissive: 0xa8f0c5,
        emissiveStrength: 0.18,
        clusterChance: 0.35
      },
      // frondes congeladas curvadas ao vento
      {
        archetype: 'ice-frond',
        count: 2600,
        minScale: 0.85,
        maxScale: 1.8,
        tint: 0x9bc7ad,
        accent: 0xd8f0df,
        emissive: 0xa8f0c5,
        emissiveStrength: 0.18
      },
      // flores baixas de tempestade
      {
        archetype: 'storm-flower',
        count: 1900,
        minScale: 0.5,
        maxScale: 0.9,
        tint: 0xb6e6c8,
        accent: 0xfffefb
      }
    ],
    fauna: [
      {
        archetype: 'walker',
        temperament: 'passive',
        count: 100,
        minScale: 0.9,
        maxScale: 1.3,
        bodyTint: 0x6da08a,
        accent: 0xc4f0d3,
        displayName: 'Pastador das Terras Altas'
      },
      {
        archetype: 'predator',
        temperament: 'hostile',
        count: 24,
        minScale: 1.1,
        maxScale: 1.45,
        bodyTint: 0x365a48,
        accent: 0x9beac1,
        emissive: 0x86f0b6,
        emissiveStrength: 0.45,
        displayName: 'Caçador do Nevoeiro'
      },
      {
        archetype: 'glider',
        temperament: 'skittish',
        count: 88,
        minScale: 0.7,
        maxScale: 1.0,
        bodyTint: 0xa5d8c0,
        accent: 0xc8f0db,
        displayName: 'Voador Cortante',
        hoverHeight: 12
      }
    ],
    water: {
      level: -8.5,
      surfaceTint: 0x90c7b6,
      depthTint: 0x174033,
      foamTint: 0xe0f5e8,
      waveAmplitude: 0.25,
      waveSpeed: 0.7,
      ice: true,
      poolRadius: 500
    },
    weather: {
      kind: 'freezing-wind',
      density: 800,
      tint: 0xcfeadc,
      accent: 0xa5d8c0,
      drift: 9.0,
      fall: 1.6,
      size: 0.5
    }
  },

  // ────────────── LUAS ──────────────

  'Verdia Minor': {
    name: 'Verdia Minor',
    fogColor: 0xd6c690,
    ambientWarm: 0xfdf6ae,
    ambientCool: 0x6f5a25,
    flora: [
      // crostas metálicas baixas
      {
        archetype: 'metal-crust',
        count: 3000,
        minScale: 0.6,
        maxScale: 1.6,
        tint: 0xc9a45b,
        accent: 0xfdf6ae
      },
      // fungos minerais pequenos
      {
        archetype: 'crack-fungus',
        count: 1200,
        minScale: 0.55,
        maxScale: 1.0,
        tint: 0xa07020,
        accent: 0xfff5c1,
        emissive: 0xffd068,
        emissiveStrength: 0.4
      }
    ],
    fauna: [
      {
        archetype: 'crawler',
        temperament: 'skittish',
        count: 60,
        minScale: 0.4,
        maxScale: 0.65,
        bodyTint: 0xb38940,
        accent: 0xfff5c1,
        displayName: 'Rastejante Metálico'
      }
    ],
    weather: {
      kind: 'metal-dust',
      density: 380,
      tint: 0xfdf6ae,
      accent: 0xb38940,
      drift: 2.5,
      fall: 0.6,
      size: 0.4
    }
  },

  'Solenne Echo': {
    name: 'Solenne Echo',
    fogColor: 0xfccdd6,
    ambientWarm: 0xffd5e0,
    ambientCool: 0x8a4055,
    flora: [
      // fotóforos orgânicos
      {
        archetype: 'photophore-bush',
        count: 2200,
        minScale: 0.7,
        maxScale: 1.4,
        tint: 0xffaecf,
        accent: 0xffe4eb,
        emissive: 0xff7ab2,
        emissiveStrength: 0.7,
        clusterChance: 0.45
      },
      // tapetes luminescentes
      {
        archetype: 'lumen-mat',
        count: 2000,
        minScale: 1.0,
        maxScale: 2.4,
        tint: 0xf4b8c3,
        accent: 0xffe4eb,
        emissive: 0xffaecf,
        emissiveStrength: 0.5,
        altitudeBias: -1
      },
      // arbustos translúcidos rosados
      {
        archetype: 'rust-shrub',
        count: 1600,
        minScale: 0.7,
        maxScale: 1.3,
        tint: 0xffaecf,
        accent: 0xfff0f4,
        emissive: 0xff9bb6,
        emissiveStrength: 0.35,
        clusterChance: 0.35
      },
      // caules curvos brilhantes
      {
        archetype: 'glow-stalk',
        count: 1400,
        minScale: 0.7,
        maxScale: 1.3,
        tint: 0xffd6df,
        accent: 0xfff0f4,
        emissive: 0xff7ab2,
        emissiveStrength: 0.7
      }
    ],
    fauna: [
      {
        archetype: 'glider',
        temperament: 'passive',
        count: 120,
        minScale: 0.6,
        maxScale: 0.95,
        bodyTint: 0xffaecf,
        accent: 0xfff0f4,
        emissive: 0xffaecf,
        emissiveStrength: 0.7,
        displayName: 'Flutuador de Luz',
        hoverHeight: 9
      },
      {
        archetype: 'crawler',
        temperament: 'passive',
        count: 72,
        minScale: 0.45,
        maxScale: 0.7,
        bodyTint: 0xc97294,
        accent: 0xffaecf,
        displayName: 'Coletor Luminal'
      },
      {
        archetype: 'spectre',
        temperament: 'hostile',
        count: 12,
        minScale: 1.0,
        maxScale: 1.3,
        bodyTint: 0xff7ab2,
        accent: 0xffe4eb,
        emissive: 0xff66a8,
        emissiveStrength: 0.85,
        displayName: 'Espectro Lúmen',
        hoverHeight: 6
      }
    ],
    water: {
      level: -9.0,
      surfaceTint: 0xffc9d6,
      depthTint: 0x52223a,
      foamTint: 0xfff0f4,
      emissive: 0xffaecf,
      emissiveStrength: 0.55,
      waveAmplitude: 0.12,
      waveSpeed: 0.4,
      poolRadius: 280
    },
    weather: {
      kind: 'photophore-pulse',
      density: 540,
      tint: 0xffaecf,
      accent: 0xfff0f4,
      drift: 0.8,
      fall: -0.3,
      size: 0.5,
      pulse: 0.55
    }
  },

  'Nyara Whisper': {
    name: 'Nyara Whisper',
    fogColor: 0xd9a07a,
    ambientWarm: 0xffd5b3,
    ambientCool: 0x3d2818,
    flora: [
      // crescimentos rachados luminescentes
      {
        archetype: 'crack-fungus',
        count: 1900,
        minScale: 0.55,
        maxScale: 1.05,
        tint: 0xc66e3a,
        accent: 0xfde2bc,
        emissive: 0xffa854,
        emissiveStrength: 0.6
      },
      // colônias minerais de sombra
      {
        archetype: 'shadow-mineral',
        count: 2200,
        minScale: 0.85,
        maxScale: 1.7,
        tint: 0x7a4326,
        accent: 0xd9a07a
      },
      // crostas mineralizadas (vida muito escassa)
      {
        archetype: 'metal-crust',
        count: 1600,
        minScale: 0.6,
        maxScale: 1.3,
        tint: 0xa05a30,
        accent: 0xf2c89d
      }
    ],
    fauna: [
      {
        archetype: 'crawler',
        temperament: 'skittish',
        count: 76,
        minScale: 0.5,
        maxScale: 0.8,
        bodyTint: 0x9b5028,
        accent: 0xffd1b8,
        emissive: 0xffa54a,
        emissiveStrength: 0.25,
        displayName: 'Criatura de Toca'
      },
      {
        archetype: 'predator',
        temperament: 'hostile',
        count: 24,
        minScale: 1.05,
        maxScale: 1.35,
        bodyTint: 0x4a230a,
        accent: 0xff8b3c,
        emissive: 0xff8b3c,
        emissiveStrength: 0.55,
        displayName: 'Predador Silencioso'
      }
    ],
    weather: {
      kind: 'radiation-haze',
      density: 660,
      tint: 0xffa54a,
      accent: 0xd9a07a,
      drift: 1.2,
      fall: -0.1,
      size: 0.55,
      pulse: 0.35
    }
  }
};

/** Lookup a biome profile by display name (matches WorldResourcePool keys). */
export function getBiomeProfile(name: string): BiomeProfile | null {
  return PROFILES[name] ?? null;
}
